import type { ContentImportItemDto } from "../types";
import type {
  FlattenedLearningPath,
  LearningPathBundle,
  LearningPathGraphIssue,
  NestedUnitDto,
} from "./types";

function asItemArray(
  value: unknown,
  path: string,
  issues: LearningPathGraphIssue[]
): ContentImportItemDto[] {
  if (value === undefined) {
    return [];
  }
  if (!Array.isArray(value)) {
    issues.push({ path, reason: "must be an array" });
    return [];
  }
  const items: ContentImportItemDto[] = [];
  for (let index = 0; index < value.length; index++) {
    const item = value[index];
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      issues.push({
        path: `${path}[${index}]`,
        reason: "must be an object",
      });
      continue;
    }
    items.push(item as ContentImportItemDto);
  }
  return items;
}

function injectUnitCode(
  item: ContentImportItemDto,
  unitCode: string,
  path: string,
  issues: LearningPathGraphIssue[]
): ContentImportItemDto {
  const existing = item.unitCode;
  if (typeof existing === "string" && existing !== unitCode) {
    issues.push({
      path,
      reason: `unitCode "${existing}" does not match parent unit "${unitCode}"`,
    });
  }
  return { ...item, unitCode };
}

function flattenNestedUnit(
  unit: NestedUnitDto,
  unitIndex: number,
  issues: LearningPathGraphIssue[]
): {
  unit: ContentImportItemDto;
  stories: ContentImportItemDto[];
  questions: ContentImportItemDto[];
  levels: ContentImportItemDto[];
} {
  const { stories, questions, levels, ...unitFields } = unit;
  const unitCode =
    typeof unitFields.code === "string" ? unitFields.code : "";
  const unitPath = `units[${unitIndex}]`;

  const nestedStories = asItemArray(
    stories,
    `${unitPath}.stories`,
    issues
  ).map((item, index) =>
    injectUnitCode(
      item,
      unitCode,
      `${unitPath}.stories[${index}]`,
      issues
    )
  );
  const nestedQuestions = asItemArray(
    questions,
    `${unitPath}.questions`,
    issues
  ).map((item, index) =>
    injectUnitCode(
      item,
      unitCode,
      `${unitPath}.questions[${index}]`,
      issues
    )
  );
  const nestedLevels = asItemArray(
    levels,
    `${unitPath}.levels`,
    issues
  ).map((item, index) =>
    injectUnitCode(item, unitCode, `${unitPath}.levels[${index}]`, issues)
  );

  return {
    unit: unitFields,
    stories: nestedStories,
    questions: nestedQuestions,
    levels: nestedLevels,
  };
}

export function flattenLearningPathBundle(
  bundle: LearningPathBundle
): { flattened: FlattenedLearningPath; issues: LearningPathGraphIssue[] } {
  const issues: LearningPathGraphIssue[] = [];
  const units: ContentImportItemDto[] = [];
  const stories: ContentImportItemDto[] = [];
  const questions: ContentImportItemDto[] = [];
  const levels: ContentImportItemDto[] = [];

  for (let index = 0; index < bundle.units.length; index++) {
    const nested = bundle.units[index];
    if (!nested) {
      continue;
    }
    const flat = flattenNestedUnit(nested, index, issues);
    units.push(flat.unit);
    stories.push(...flat.stories);
    questions.push(...flat.questions);
    levels.push(...flat.levels);
  }

  return {
    flattened: {
      space: {
        name: bundle.space.name,
        title: bundle.space.title,
        body: bundle.space.body,
      },
      units,
      stories,
      questions,
      levels,
    },
    issues,
  };
}
